import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, Lock, User, Chrome, CheckCircle2, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { GlassContainer, ShineButton, GradientText } from './UI';
import { auth, googleProvider, appleProvider, db } from '../utils/firebase';
import {
   signInWithPopup,
   signInWithEmailAndPassword,
   createUserWithEmailAndPassword,
   updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { storage } from '../utils/storage';

interface AuthModalProps {
   mode: 'login' | 'signup' | null;
   onClose: () => void;
   onSwitch: () => void;
   onLoginSuccess?: (email?: string) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ mode, onClose, onSwitch, onLoginSuccess }) => {
   const navigate = useNavigate();
   const [isLoading, setIsLoading] = useState(false);
   const [isSuccess, setIsSuccess] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [formData, setFormData] = useState({ email: '', password: '', name: '' });
   const [showPassword, setShowPassword] = useState(false);

   // Reset state when mode changes or opens
   useEffect(() => {
      if (mode) {
         setIsSuccess(false);
         setIsLoading(false);
         setError(null);
         setFormData({ email: '', password: '', name: '' });
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = 'unset';
      }
      return () => { document.body.style.overflow = 'unset'; }
   }, [mode]);

   if (!mode) return null;

   const handleSuccess = (userEmail?: string) => {
      setIsSuccess(true);
      localStorage.setItem('lumina_session', 'true');
      window.dispatchEvent(new Event('auth-change'));

      setTimeout(() => {
         onClose();
         if (onLoginSuccess) {
            onLoginSuccess(userEmail);
         }
      }, 1500);
   };

   const handleError = (err: any) => {
      console.error('Auth error:', err);
      let message = 'Authentication failed. Please try again.';
      if (err.code === 'auth/email-already-in-use') message = 'Email already in use.';
      else if (err.code === 'auth/invalid-email') message = 'Invalid email address.';
      else if (err.code === 'auth/weak-password') message = 'Password should be at least 6 characters.';
      else if (err.code === 'auth/user-not-found') message = 'User not found.';
      else if (err.code === 'auth/wrong-password') message = 'Incorrect password.';
      else if (err.code === 'auth/popup-closed-by-user') message = 'Popup closed. Please try again.';
      setError(message);
      setIsLoading(false);
   };

   // Google Sign In
   const handleGoogleSignIn = async () => {
      setIsLoading(true);
      setError(null);
      try {
         const result = await signInWithPopup(auth, googleProvider);
         // Save new user to admin panel
         await saveUserToFirebase(result.user);
         handleSuccess(result.user.email || undefined);
      } catch (err) {
         handleError(err);
      }
   };

   // Apple/iCloud Sign In
   const handleAppleSignIn = async () => {
      setIsLoading(true);
      setError(null);
      try {
         const result = await signInWithPopup(auth, appleProvider);
         // Save new user to admin panel
         await saveUserToFirebase(result.user);
         handleSuccess(result.user.email || undefined);
      } catch (err) {
         handleError(err);
      }
   };

   // Email/Password Submit
   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      setError(null);

      try {
         if (mode === 'signup') {
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            if (formData.name) {
               await updateProfile(userCredential.user, { displayName: formData.name });
            }
            // Save new user to Firebase
            await saveUserToFirebase(userCredential.user, formData.name);
         } else {
            await signInWithEmailAndPassword(auth, formData.email, formData.password);
         }
         handleSuccess(formData.email);
      } catch (err) {
         handleError(err);
      }
   };

   // Save user to Firebase Firestore
   const saveUserToFirebase = async (firebaseUser: any, displayName?: string) => {
      try {
         const userRef = doc(db, 'users', firebaseUser.uid);
         const userDoc = await getDoc(userRef);

         // Only create if user doesn't exist
         if (!userDoc.exists()) {
            const userData = {
               id: firebaseUser.uid,
               name: displayName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
               email: firebaseUser.email || '',
               role: 'user' as const,
               avatar: firebaseUser.photoURL || '',
               joinedDate: new Date().toISOString().split('T')[0],
               status: 'active' as const
            };

            await setDoc(userRef, userData);
            console.log('✅ New user saved to Firebase:', firebaseUser.email);

            // Also save to localStorage as backup
            storage.addUser(userData);
         }
      } catch (error) {
         console.error('❌ Error saving user to Firebase:', error);
      }
   };

   return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
         {/* Backdrop */}
         <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity animate-fade-in-up duration-300"
            onClick={onClose}
         />

         {/* Modal Content */}
         <GlassContainer className="w-full max-w-md p-8 relative z-10 animate-fade-in-up border-white/40 shadow-2xl bg-white/80">
            <button
               onClick={onClose}
               aria-label="Close modal"
               className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-full"
            >
               <X size={20} />
            </button>

            {/* Success State */}
            {isSuccess ? (
               <div className="text-center py-12 animate-fade-in-up">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-500 animate-bounce shadow-lg shadow-green-500/20">
                     <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome Aboard!</h3>
                  <p className="text-gray-500">Redirecting to your dashboard...</p>
               </div>
            ) : (
               <>
                  {/* Header */}
                  <div className="text-center mb-8">
                     <h2 className="text-3xl font-bold mb-2 text-gray-900">
                        {mode === 'login' ? 'Welcome Back' : <><GradientText>Join</GradientText> Nescotech</>}
                     </h2>
                     <p className="text-gray-500 text-sm">
                        {mode === 'login' ? 'Enter your credentials to access your workspace.' : 'Start your journey with the future of digital.'}
                     </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                     <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm animate-fade-in-up">
                        <AlertCircle size={16} />
                        {error}
                     </div>
                  )}

                  {/* Social Logins */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                     <button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        aria-label="Sign in with Google"
                        className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-brand-lime/30 transition-all text-sm font-bold text-gray-600 shadow-sm hover:shadow-md disabled:opacity-50"
                     >
                        <Chrome size={18} aria-hidden="true" /> Google
                     </button>
                     <button
                        onClick={handleAppleSignIn}
                        disabled={isLoading}
                        aria-label="Sign in with iCloud"
                        className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-brand-lime/30 transition-all text-sm font-bold text-gray-600 shadow-sm hover:shadow-md disabled:opacity-50"
                     >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                        iCloud
                     </button>
                  </div>

                  <div className="relative mb-6">
                     <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                     <div className="relative flex justify-center text-xs uppercase"><span className="bg-white/80 px-2 text-gray-400 font-bold backdrop-blur-sm">Or continue with</span></div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                     {mode === 'signup' && (
                        <div className="space-y-1 animate-fade-in-up">
                           <label className="text-xs font-bold text-gray-700 ml-1 uppercase">Full Name</label>
                           <div className="relative group">
                              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-lime transition-colors" />
                              <input
                                 type="text"
                                 placeholder="John Doe"
                                 className="w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-brand-lime focus:ring-4 focus:ring-brand-lime/10 outline-none transition-all text-sm font-medium shadow-sm"
                                 required
                                 value={formData.name}
                                 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              />
                           </div>
                        </div>
                     )}

                     <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700 ml-1 uppercase">Email Address</label>
                        <div className="relative group">
                           <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-lime transition-colors" />
                           <input
                              type="email"
                              placeholder="name@company.com"
                              className="w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-brand-lime focus:ring-4 focus:ring-brand-lime/10 outline-none transition-all text-sm font-medium shadow-sm"
                              required
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                           />
                        </div>
                     </div>

                     <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700 ml-1 uppercase">Password</label>
                        <div className="relative group">
                           <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-lime transition-colors" />
                           <input
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              className="w-full pl-11 pr-12 py-3 rounded-xl bg-white border border-gray-200 focus:border-brand-lime focus:ring-4 focus:ring-brand-lime/10 outline-none transition-all text-sm font-medium shadow-sm"
                              required
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                           />
                           <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-lime transition-colors"
                           >
                              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                           </button>
                        </div>
                     </div>

                     {mode === 'login' && (
                        <div className="flex justify-end">
                           <a href="#" className="text-xs font-bold text-brand-lime hover:underline hover:text-brand-yellow transition-colors">Forgot password?</a>
                        </div>
                     )}

                     <ShineButton className={`w-full !py-3 !mt-6 shadow-xl ${isLoading ? 'opacity-70 pointer-events-none' : ''}`}>
                        {isLoading ? (
                           <span className="flex items-center gap-2">Processing...</span>
                        ) : (
                           <span className="flex items-center gap-2">
                              {mode === 'login' ? 'Log In' : 'Create Account'} <ArrowRight size={16} />
                           </span>
                        )}
                     </ShineButton>
                  </form>

                  <div className="mt-6 text-center text-sm text-gray-500 bg-gray-50/50 py-3 rounded-lg border border-gray-100">
                     {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
                     <button onClick={onSwitch} className="font-bold text-brand-dark hover:text-brand-lime hover:underline transition-colors">
                        {mode === 'login' ? 'Sign up' : 'Log in'}
                     </button>
                  </div>
               </>
            )}
         </GlassContainer>
      </div>
   );
};
